import { Injectable } from "@angular/core";
import { ResourceAmenityControllerService } from "@booking/angular-booking-generic-service";
import { environment } from "@env/environment";
import { MulTranslationService } from "../core/services/mul-translation/mul-translation.service";
import { MONTH_EXPIRED_AMENITIES_TRANSLATE } from "@shared/constant";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class AmenitiesService {
  baseUrl = environment.BASE_PATH;
  amenitiesTranslated: { [resource_id: number]: { [key: string]: string } } = {};
  isLoading: boolean = false;
  private dataPromise: Promise<void> | null = null;
  private language: string;

  constructor(
    private amenityControllerService: ResourceAmenityControllerService,
    private mulTranslationService: MulTranslationService,
    private translateService: TranslateService,
  ) {
    this.language = this.mulTranslationService.getKeyLocalUserProfile('language')?.toLowerCase() || localStorage.getItem('unauthLang') || 'vi';
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
        this.updateLanguage(event.lang);
      });
      
    this.initializeData();
  }

  private initializeData(): void {
    if (this.isAmenitiesDataExpired()) {
      this.getAmenitiesByLanguage();
    } else {
      const cached = JSON.parse(localStorage.getItem('amenities-translate-' + this.language)!);
      this.amenitiesTranslated = this.transformToKeyValue(cached.data);
    }
  }

  getAmenitiesByLanguage(): Promise<void> {
    if (this.isLoading) {
      return this.dataPromise!;
    }

    this.isLoading = true;
    this.dataPromise = new Promise((resolve, reject) => {
      this.amenityControllerService
        .getAmenitiesByResourceAndLanguageCode(this.language, undefined)
        .subscribe({
          next: (res: any) => {
            if (res && res.success && res.data && res.data.length > 0) {
              const now = new Date().getTime();
              const oneMonth = MONTH_EXPIRED_AMENITIES_TRANSLATE * 30 * 24 * 60 * 60 * 1000;
              localStorage.setItem(
                'amenities-translate-' + this.language,
                JSON.stringify({ data: res.data, expiredTime: now + oneMonth })
              );
              this.amenitiesTranslated = this.transformToKeyValue(res.data);
            } 
            this.isLoading = false;
            this.dataPromise = null;
            resolve();
          },
          error: (err) => {
            this.isLoading = false;
            this.dataPromise = null;
            reject(err);
          }
        });
    });

    return this.dataPromise;
  }

  isAmenitiesDataExpired(): boolean {
    const raw = localStorage.getItem('amenities-translate-' + this.language);
    if (!raw) return true;

    try {
      const stored = JSON.parse(raw);
      const now = new Date().getTime();
      return !stored.expiredTime || now > stored.expiredTime;
    } catch (e) {
      return true;
    }
  }

  //map to object key value (bigger data)
  private transformToKeyValue(data: { resource_id: number; amenities: { key: string; value: string }[] }[]): {
    [resource_id: number]: { [key: string]: string };
  } {
    return data.reduce((map, item) => {
      map[item.resource_id] = item.amenities.reduce((amenityMap, amenity) => {
        amenityMap[amenity.key] = amenity.value;
        return amenityMap;
      }, {} as { [key: string]: string });
      return map;
    }, {} as { [resource_id: number]: { [key: string]: string } });
  }

  async getValueByKey(key: string, resource_id: number): Promise<string> {
    if (this.isAmenitiesDataExpired() && !this.isLoading) {
      await this.getAmenitiesByLanguage();
    }
    if (this.isLoading && this.dataPromise) {
      await this.dataPromise;
    }
    return this.amenitiesTranslated?.[resource_id]?.[key] || '';
  }

  getTranslatedAmenitySync(key: string, resource_id: number): string {
    if (!this.amenitiesTranslated || !this.amenitiesTranslated[resource_id]) {
      try {
        const cached = localStorage.getItem('amenities-translate-' + this.language);
        if (cached) {
          const parsed = JSON.parse(cached);
          this.amenitiesTranslated = this.transformToKeyValue(parsed.data);
        }
      } catch (err) {
        console.error('Error parse cache amenities:', err);
        return '';
      }
    }
    return this.amenitiesTranslated?.[resource_id]?.[key] || '';
  }

  async getValueFromAllResources(key: string): Promise<string> {
    if (this.isAmenitiesDataExpired() && !this.isLoading) {
      await this.getAmenitiesByLanguage();
    }
    if (this.isLoading && this.dataPromise) {
      await this.dataPromise;
    }
  
    const normalizedKey = key.toLowerCase();
    for (let resource_id = 0; resource_id <= 5; resource_id++) {
      const value = this.amenitiesTranslated[resource_id]?.[ resource_id == 0 ? key : normalizedKey];
      if (value) return value;
    }
  
    return '';
  }

  updateLanguage(newLang: string): void {
    this.language = newLang.toLowerCase() || 'vi';
    this.amenitiesTranslated = {}; //clear old data
    this.initializeData();
  }
}